import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { errMsg } from "@/lib/api";
import { useAuth, RETURN_TO_KEY, SESSION_EXPIRED_KEY } from "@/context/AuthContext";
import { Field, inputCls } from "@/components/common";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";

const REMEMBER_KEY = "neec_remember_email";
const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function Login() {
  const { user, login, verify2fa } = useAuth();
  const [stage, setStage] = useState("password"); // password | 2fa
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const saved = localStorage.getItem(REMEMBER_KEY) || "";
  const [email, setEmail] = useState(saved);
  const [remember, setRemember] = useState(!!saved);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [lockSeconds, setLockSeconds] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (lockSeconds <= 0) return;
    const t = setInterval(() => setLockSeconds((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [lockSeconds > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  if (user) {
    const back = sessionStorage.getItem(RETURN_TO_KEY);
    return <Navigate to={back && back.startsWith("/") ? back : "/"} replace />;
  }
  const expired = sessionStorage.getItem(SESSION_EXPIRED_KEY) === "1";

  const submit = async (e) => {
    e.preventDefault();
    if (lockSeconds > 0) return;
    setBusy(true); setError("");
    try {
      const r = await login(email, password);
      if (remember) localStorage.setItem(REMEMBER_KEY, email.trim().toLowerCase()); else localStorage.removeItem(REMEMBER_KEY);
      if (r?.requires_2fa) { setStage("2fa"); setPassword(""); }
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.code === "locked") { setLockSeconds(d.retry_after || 600); setError(""); } else setError(errMsg(err));
    } finally { setBusy(false); }
  };
  const submitCode = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await verify2fa(useRecovery ? { recovery_code: code } : { code: code.replace(/\s/g, "") });
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.code === "2fa_expired") { setStage("password"); setCode(""); setError(d.message); }
      else if (d?.code === "locked") { setLockSeconds(d.retry_after || 60); setError(""); }
      else setError(errMsg(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0B192C] overflow-x-hidden">
      <div className="hidden lg:flex flex-col justify-between p-14 xl:p-16 text-white relative overflow-hidden">
        <div className="absolute -right-32 -top-32 w-[520px] h-[520px] rounded-full bg-[#15253F]" />
        <div className="absolute -left-20 bottom-10 w-72 h-72 rounded-full bg-orange-500/10" />
        <div className="relative"><Brand light /></div>
        <div className="relative max-w-2xl">
          <h1 className="text-4xl xl:text-5xl font-bold leading-[1.12] tracking-tight" data-testid="hero-headline">
            From customer to quote.<br />From job to payment.<br />From expenses to the big picture.<br />
            <span className="text-orange-500">NEEC Service keeps your business together.</span>
          </h1>
          <p className="mt-8 text-slate-300 max-w-lg text-base leading-relaxed" data-testid="hero-subcopy">
            Simple business management for service companies. Manage customers, quotes, jobs, invoices, payments, expenses and reports in one place.
          </p>
        </div>
        <div className="relative text-xs text-slate-500" data-testid="hero-footer">Built for service businesses that need a simple way to stay organized and get paid.</div>
      </div>
      <div className="flex items-center justify-center p-5 sm:p-10 bg-white lg:rounded-l-[2rem]">
        <div className="w-full max-w-sm fade-up">
          <div className="mb-8 p-4 rounded-2xl bg-slate-50 border border-slate-200" data-testid="tenant-card"><Brand /></div>
          <h2 className="text-3xl font-bold text-slate-900">Welcome back</h2>
          <p className="text-sm text-slate-500 mb-6">Sign in to your business account.</p>
          {expired && <p className="text-sm text-slate-700 bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 mb-4" data-testid="session-expired-notice">Your session expired. Please sign in again — we'll take you back to where you were.</p>}
          {stage === "2fa" ? (
          <form onSubmit={submitCode} className="space-y-4" data-testid="twofa-login-form">
            <p className="text-sm text-slate-600" data-testid="twofa-login-prompt">{useRecovery ? "Enter one of your unused recovery codes." : "Enter the 6-digit code from your authenticator app."}</p>
            <Field label={useRecovery ? "Recovery code" : "Authenticator code"}><input data-testid="twofa-login-code" className={inputCls} inputMode={useRecovery ? "text" : "numeric"} maxLength={useRecovery ? 20 : 6} value={code} onChange={(e) => setCode(e.target.value)} required autoFocus autoComplete="one-time-code" /></Field>
            {lockSeconds > 0 && <p className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2" data-testid="login-lockout">Too many attempts. Try again in <span className="font-mono font-semibold" data-testid="lockout-countdown">{fmt(lockSeconds)}</span>.</p>}
            {error && lockSeconds <= 0 && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={busy || lockSeconds > 0} className="w-full h-12 btn-orange text-base" data-testid="twofa-login-submit">{busy ? "Verifying..." : "Verify"}</Button>
            <div className="flex justify-between text-sm">
              <button type="button" className="text-orange-600 font-semibold" onClick={() => { setUseRecovery(!useRecovery); setCode(""); setError(""); }} data-testid="twofa-login-toggle-recovery">{useRecovery ? "Use authenticator code" : "Use a recovery code"}</button>
              <button type="button" className="text-slate-500" onClick={() => { setStage("password"); setCode(""); setError(""); }} data-testid="twofa-login-back">Back</button>
            </div>
          </form>
          ) : (
          <form onSubmit={submit} className="space-y-4" method="post" action="#">
            <Field label="Email"><input data-testid="login-email-input" type="email" name="username" id="login-email" inputMode="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" spellCheck={false} autoCapitalize="none" /></Field>
            <Field label="Password"><input data-testid="login-password-input" type="password" name="password" id="login-password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" /></Field>
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-slate-600 cursor-pointer select-none">
                <input type="checkbox" className="h-4 w-4 rounded border-slate-300 accent-orange-500" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="remember-email-checkbox" />
                Remember my email
              </label>
              <Link to="/forgot-password" className="font-semibold text-orange-600 hover:text-orange-700" data-testid="forgot-password-link">Forgot password?</Link>
            </div>
            {lockSeconds > 0 && (
              <p className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2" data-testid="login-lockout">Too many unsuccessful attempts.<br />Try again in <span className="font-mono font-semibold" data-testid="lockout-countdown">{fmt(lockSeconds)}</span>.</p>
            )}
            {error && lockSeconds <= 0 && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={busy || lockSeconds > 0} className="w-full h-12 btn-orange text-base" data-testid="login-submit-button">{busy ? "Signing in..." : lockSeconds > 0 ? `Locked (${fmt(lockSeconds)})` : "Sign In"}</Button>
          </form>
          )}
          <p className="mt-8 text-xs text-slate-400 lg:hidden" data-testid="mobile-footer">Built for service businesses that need a simple way to stay organized and get paid.</p>
        </div>
      </div>
    </div>
  );
}
